import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/homeData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const popularArticles = articles.slice(0, 6);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/98 backdrop-blur-lg border-b-2 border-background-300/50 shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
          : 'bg-gradient-to-b from-black/50 to-transparent'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Home */}
          <Link
            to="/"
            className={`font-heading text-lg md:text-xl font-bold whitespace-nowrap transition-colors duration-300 cursor-pointer ${
              scrolled ? 'text-foreground-950' : 'text-background-50 drop-shadow-sm'
            }`}
          >
            ACB Spreadsheet
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {/* Home */}
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/95 hover:text-background-50 hover:bg-background-50/15'
              }`}
            >
              <i className="ri-home-4-line mr-1.5"></i>
              Home
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  setGuideDropdownOpen(false);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-background-50/95 hover:text-background-50 hover:bg-background-50/15'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-sm ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-xl border border-background-200/70 py-2 overflow-hidden animate-[fadeIn_0.15s_ease-out] shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                  <div className="max-h-[420px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className={`${cat.icon} text-foreground-500 text-sm`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Shopping Guide Dropdown */}
            <div ref={guideDropdownRef} className="relative">
              <button
                onClick={() => {
                  setGuideDropdownOpen(!guideDropdownOpen);
                  setDropdownOpen(false);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-background-50/95 hover:text-background-50 hover:bg-background-50/15'
                }`}
              >
                <i className="ri-book-open-line mr-1"></i>
                Shopping Guide
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-sm ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {guideDropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-80 bg-background-50 rounded-xl border border-background-200/70 py-2 overflow-hidden animate-[fadeIn_0.15s_ease-out] shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                  <div className="px-4 py-2.5 border-b border-background-200/50">
                    <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                      Buying Guides &amp; Articles
                    </span>
                  </div>
                  <div className="max-h-[360px] overflow-y-auto">
                    {popularArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/guide/${article.slug}`}
                        onClick={() => setGuideDropdownOpen(false)}
                        className="flex items-start gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-article-line text-foreground-500 text-sm"></i>
                        </span>
                        <div className="min-w-0">
                          <span className="block leading-snug line-clamp-2">{article.title}</span>
                          <span className="text-xs text-foreground-400 mt-0.5">{article.readTime}</span>
                        </div>
                      </Link>
                    ))}
                    <Link
                      to="/"
                      onClick={() => setGuideDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-accent-600 hover:text-accent-700 hover:bg-accent-50 transition-colors cursor-pointer border-t border-background-200/50 mt-1"
                    >
                      <i className="ri-arrow-right-line text-sm"></i>
                      View All Articles on Homepage
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* AllChinaBuy Sheet */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/95 hover:text-background-50 hover:bg-background-50/15'
              }`}
            >
              AllChinaBuy Sheet
            </a>

            {/* Buy It Now CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-950' : 'text-background-50 drop-shadow-sm'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t-2 border-background-300/50 animate-[fadeIn_0.2s_ease-out] max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            {/* Home */}
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-home-4-line text-sm"></i>
              </span>
              Home
            </Link>

            {/* Divider */}
            <div className="pt-3 pb-1">
              <span className="px-3 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                Product Categories
              </span>
            </div>

            {/* Categories Grid on Mobile */}
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${cat.icon} text-foreground-500 text-xs`}></i>
                  </span>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>

            {/* Divider */}
            <div className="pt-3 pb-1">
              <span className="px-3 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                Shopping Guides
              </span>
            </div>

            {/* Article links on mobile */}
            {popularArticles.map((article) => (
              <Link
                key={article.id}
                to={`/guide/${article.slug}`}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-article-line text-foreground-500 text-xs"></i>
                </span>
                <span className="truncate">{article.title}</span>
                <span className="text-[10px] text-foreground-400 ml-auto flex-shrink-0">{article.readTime}</span>
              </Link>
            ))}

            {/* CTA buttons */}
            <div className="pt-3 space-y-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg text-sm font-semibold text-foreground-700 bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-sm"></i>
                AllChinaBuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-shopping-cart-line text-sm"></i>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}